/** Manages RMA list data fetching with dual-backend support (Laravel proxy / rms-api).
 *  Before routing, loads BETA_RMS_API_MANAGE_RETURNS via CurrentPortalStore so the flag is
 *  not read before user features are available. When enabled, requests use rms-api
 *  (RmsApiConnectionManager); otherwise the Laravel proxy.
 */
// eslint-disable-next-line
import { injectable } from "inversify";
import { rootContainer, TYPE } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import { IFetchListArgs, IPagedData } from "../../stores/abstract-paged-data-store";
import { createPagedRequestURLSearchParams } from "../helpers";
import { RMA_STATUS } from "../../pages/manage-returns/rma-page/rma-types";
import { IResolutionCategoryPreset } from "../../pages/policies/policy-types";
import RmsApiConnectionManager from "../rms-api-connection-manager";
import currentPortalStore from "../../stores/current-portal-store";
import { FEATURES } from "../../models/Plan";

/** This is not one to one Rma model's status, it computed on server and it considers shipment status too */
export enum RMA_STATUS_TRANSLATION_KEY {
    PENDING = 'PENDING',
    DONE = 'DONE',
    APPROVED = 'APPROVED',
    RECEIVED = 'RECEIVED',
    REJECTED = 'REJECTED',
    CANCELED = 'CANCELED',
}

export enum RMA_PROVIDERS {
    REFUND_ID = 'refundid',
    REVENI = 'reveni',
    RISE = 'rise',
}

export enum RESTOCK_VALIDATED_TYPE {
    ALL = 'all',
    VALIDATED = 'filter_restock_is_validated',
    NOT_VALIDATED = 'filter_restock_is_not_validated',
    PARTIALLY_VALIDATED = 'filter_restock_is_partially_validated'
}

export enum PAYMENT_STATUS_TYPE {
    ALL = 'all',
    NOT_REFUNDED = 'not_refunded',
    REFUNDED = 'refunded',
    NOT_PAID = 'not_paid',
    PAID = 'paid',
    EXPIRING = 'expiring',
}

export enum RESERVED_ITEM_EXPIRATION_TYPE {
    ALL = 'all',
    DATE_RANGE = 'date_range',
    DAYS_REMAINING = 'days_remaining',
    EXPIRED = 'expired',
}


export enum IRmaListTab {
    STATUS_GROUP_CLOSED = 'CLOSED',
    STATUS_GROUP_OPEN = 'OPEN',
    STATUS_GROUP_ALL = 'ALL',
}
export interface IRmaListItem {
    id: number;
    order_name: string;
    _customer_name: string;
    amount: number;
    is_gift: boolean;
    is_out_of_policy: boolean;
    last_unresolved_error: string | null;
    status: RMA_STATUS;
    reserve_item_count: number;
    _release_date: string | null;
    is_returngo_resolution: boolean;
    has_exchange: boolean;
    no_offers: number | null;
    date_to_show: string;
    _order_url: string,
    _customer_url: string,
    _status: RMA_STATUS_TRANSLATION_KEY,
    _images_urls: string[],
    _reserve_until_date: string | null,
    _item_reservation_timeframe: number,
    _is_instant_exchange: boolean,
    _exchange_draft_order_id: number | null,
    _resolution_name: string,
    _refund_provider: RMA_PROVIDERS | null,
    _credit_integration_provider: RMA_PROVIDERS | null,
}

export interface IFetchRmaListArgs extends IFetchListArgs {
    search_input: string;
    search_exact_match: boolean;
    filter_resolution_id: number | null;
    filter_return_reason_id: number | null;
    filter_restock_validated_type: RESTOCK_VALIDATED_TYPE;
    filter_date_range: string | null;
    filter_status: string | null;
    filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE;
    filter_reserve_item_expiration_input: string;
    filter_payment_status_type: PAYMENT_STATUS_TYPE;
    status_group: IRmaListTab;
}

export interface IReturnReason {
    id: number,
    name: string,
}

export interface IRmaListFilterOptions {
    resolutions: IResolutionCategoryPreset[],
    return_reasons: IReturnReason[],
}

@injectable()
class RmaListService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);
    private readonly rmsApiConnection: RmsApiConnectionManager = rootContainer.get<RmsApiConnectionManager>(TYPE.RmsApiConnectionManager);

    private isRmsApiManageReturnsEnabled(): boolean {
        return currentPortalStore.isFeatureAllowed(FEATURES.BETA_RMS_API_MANAGE_RETURNS);
    }

    public async fetchRmaList(args: IFetchRmaListArgs): Promise<IPagedData<IRmaListItem>> {
        await currentPortalStore.loadFeatures([FEATURES.BETA_RMS_API_MANAGE_RETURNS]);

        const {
            page,
            perPage,
            sortBy,
            sortDir,
            search_exact_match,
            search_input,
            filter_resolution_id,
            filter_return_reason_id,
            filter_restock_validated_type,
            filter_date_range,
            filter_status,
            filter_reserve_item_expiration,
            filter_reserve_item_expiration_input,
            filter_payment_status_type,
            status_group,
        } = args;
        const searchParams = createPagedRequestURLSearchParams(page, perPage, sortBy, sortDir);
        if (search_input) {
            searchParams.append('search_input', search_input);
        }
        if (search_exact_match) {
            searchParams.append('search_exact_match', '1');
        }
        if (filter_resolution_id) {
            searchParams.append('resolution_id', filter_resolution_id.toString());
        }
        if (filter_return_reason_id) {
            searchParams.append('return_reason_id', filter_return_reason_id.toString());
        }
        if (filter_restock_validated_type) {
            searchParams.append('restock_validated_type', filter_restock_validated_type);
        }
        if (filter_date_range) {
            searchParams.append('date_range', filter_date_range);
        }
        if (filter_status) {
            searchParams.append('status', filter_status);
        }
        if (filter_reserve_item_expiration) {
            searchParams.append('reserve_item_expiration', filter_reserve_item_expiration);
        }
        if (filter_reserve_item_expiration_input) {
            searchParams.append('reserve_item_expiration_input', filter_reserve_item_expiration_input);
        }
        if (filter_payment_status_type) {
            searchParams.append('payment_status_type', filter_payment_status_type);
        }
        if (status_group) {
            searchParams.append("status_group", status_group);
        }

        if (this.isRmsApiManageReturnsEnabled()) {
            const res = await this.rmsApiConnection.get<IApiResponse<IPagedData<IRmaListItem>>>(
                `/manage-returns?${searchParams.toString()}`
            );
            return res.payload;
        }

        const res = await this.httpService.get<IApiResponse<IPagedData<IRmaListItem>>>(
            `/rms-api/manage-returns/rma-list?${searchParams.toString()}`
        );
        return res.payload;
    }

    public async fetchFilters(): Promise<IRmaListFilterOptions> {
        const res = await this.httpService.get<IApiResponse<IRmaListFilterOptions>>(`/rms-api/manage-returns/filters`);
        return res.payload;
    }

    public async fetchRmaListByIds(csvIdList: string): Promise<IRmaListItem[]> {
        const res = await this.httpService.get<IApiResponse<IRmaListItem[]>>(`/rms-api/manage-returns/rma-list-by-ids?ids=${csvIdList}`);
        return res.payload;
    }

}


export default RmaListService;
